import axios from "axios";
import {
  adminApiBase,
  adminProxyTarget,
  paymentApiBase,
  userApiBase,
} from "../config/urls";
// import { verifyLogin, signToken, createUser } from "../mocks/service.memory";

// User backend (auth, user, project)
const API_BASE = userApiBase();
const ADMIN_API_BASE = adminApiBase();
// Password reset endpoints share the same user backend base
const PASS_API_BASE = API_BASE;

// Payment / Stripe backend
const PAYMENT_API_BASE = paymentApiBase();

/** Path on the admin API (GET/POST body for terms CMS). */
const ADMIN_TERMS_PATH = "/api/admin/terms";

/**
 * Public terms are served by adminb-backend. In local dev, `setupProxy.js` forwards
 * same-origin `/api/admin/*` to that server. Using an absolute `REACT_APP_ADMIN_API_BASE`
 * URL from the browser skips the proxy and often fails with Axios "Network Error" when the
 * remote host does not allow CORS from localhost.
 */
const resolveAdminTermsApiUrl = (): string => {
  const forceAbsolute =
    process.env.REACT_APP_ADMIN_TERMS_USE_ABSOLUTE_URL === "true";
  if (
    typeof window !== "undefined" &&
    process.env.NODE_ENV === "development" &&
    !forceAbsolute
  ) {
    return ADMIN_TERMS_PATH;
  }
  const base = String(ADMIN_API_BASE || "").replace(/\/$/, "");
  return base ? `${base}${ADMIN_TERMS_PATH}` : ADMIN_TERMS_PATH;
};

const parseTermsPayload = (
  response: { data?: unknown }
): { content: string; updatedAt?: string | null } => {
  const payload = (response.data ?? {}) as Record<string, unknown>;
  if (payload && typeof payload === "object" && "content" in payload) {
    return {
      content: String(payload.content ?? ""),
      updatedAt:
        (payload.updatedAt as string | undefined) ??
        (payload.updated_at as string | undefined) ??
        null,
    };
  }
  const success = payload.success as boolean | undefined;
  const data = payload.data as Record<string, unknown> | undefined;
  const message = payload.message as string | undefined;
  if (success && data && typeof data === "object" && "content" in data) {
    return { content: String(data.content ?? "") };
  }
  if (success && data) return { content: "" };
  throw new Error(message || "Failed to fetch terms");
};

/** Shown when CMS and remote admin API are unavailable (dev without admin server, etc.). */
const fetchTermsStaticFallback = async (): Promise<{ content: string } | null> => {
  if (typeof window === "undefined") return null;
  const prefix = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const path = "/terms-and-conditions.txt";
  const url = prefix ? `${prefix}${path}` : path;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    return text ? { content: text } : null;
  } catch {
    return null;
  }
};

/** Try same-origin proxy URL first, then explicit admin host (not user API base). */
const getTermsRequestUrls = (): string[] => {
  const urls: string[] = [];
  urls.push(resolveAdminTermsApiUrl());
  const adminOnly = String(process.env.REACT_APP_ADMIN_API_BASE || "").replace(
    /\/$/,
    ""
  );
  if (adminOnly) {
    const remote = `${adminOnly}${ADMIN_TERMS_PATH}`;
    if (!urls.includes(remote)) urls.push(remote);
  } else if (process.env.NODE_ENV === "development") {
    const devAdminBase = adminProxyTarget().replace(/\/$/, "");
    const localAdmin = `${devAdminBase}${ADMIN_TERMS_PATH}`;
    if (!urls.includes(localAdmin)) urls.push(localAdmin);
  }
  return urls;
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || "";
    const isLoginRequest = url.includes("/api/auth/Login");

    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("userId");
      localStorage.removeItem("projectId");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

const isNetworkError = (err: any) => !err?.response || err?.message === "Network Error";

// Provide lowercase variant for case-sensitive backends
const altCasePath = (path: string) => {
  if (path.startsWith("/api/auth/") || path.startsWith("/api/user/")) {
    const last = path.split("/").pop() || "";
    const base = path.substring(0, path.length - last.length);
    return `${base}${last.toLowerCase()}`;
  }
  return path;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Authentication APIs
export const authRegister = async (payload: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organisation: string;
  username: string;
  acceptTandC: boolean;
  newsLetterSubs: boolean;
  roleDescription?: string;
  usagePurpose?: string;
}) => {
  try {
    // Step 1: Create user account
    const res = await postApi(
      "/api/user/Create",
      {
        email: payload.email,
        password: payload.password,
        firstName: payload.firstName,
        lastName: payload.lastName,
        organisation: payload.organisation,
        username: payload.username,
        acceptTandC: payload.acceptTandC,
        newsLetterSubs: payload.newsLetterSubs,
        ...(payload.roleDescription !== undefined && {
          roleDescription: payload.roleDescription,
        }),
        ...(payload.usagePurpose !== undefined && {
          usagePurpose: payload.usagePurpose,
        }),
      },
      { "Content-Type": "application/json" }
    );

    const { success, message } = res.data ?? {};
    if (!success) throw new Error(message || "Registration failed");

    // Step 2: Immediately login to get token
    const loginRes = await postApi(
      "/api/auth/Login",
      { email: payload.email, password: payload.password },
      { "Content-Type": "application/json" }
    );

    const { success: loginOk, data, message: loginMsg } = loginRes.data ?? {};
    if (!loginOk) throw new Error(loginMsg || "Auto-login failed after registration");

    return { token: data.token, user: data.user };
  } catch (err: any) {
    // For real integration testing, do not fabricate local users/tokens.
    if (isNetworkError(err)) {
      throw new Error("Registration failed: backend unreachable. Check REACT_APP_API_BASE and ensure toc-user-backend is running on port 3001.");
    }
    throw new Error(err.response?.data?.message || err.message || "Registration failed");
  }
};

export const authLogin = async (payload: { email: string; password: string }) => {
  try {
    const response = await postApi(
      "/api/auth/Login",
      payload,
      { "Content-Type": "application/json" }
    );

    const { success, data, message } = response.data;

    if (!success) throw new Error(message || "Login failed");

    return { token: data.token, user: data.user };
  } catch (err: any) {
    // For real integration testing, do not fabricate local tokens.
    if (isNetworkError(err)) {
      throw new Error("Login failed: backend unreachable. Check REACT_APP_API_BASE and ensure toc-user-backend is running on port 3001.");
    }
    throw new Error(err.response?.data?.message || err.message || "Login failed");
  }
};

export const forgotPassword = async (data: { email: string }) => {
  // Unified endpoint for requesting a password reset code
  const response = await axios.post(`${PASS_API_BASE}/api/auth/password.Reset`, {
    email: data.email,
    action: "request-reset",
  });
  return response.data;
};

export const resetPassword = async (data: {
  email: string;
  token: string;
  newPassword: string;
}) => {
  // Unified endpoint for verifying token and resetting password
  const response = await axios.post(`${PASS_API_BASE}/api/auth/password.Reset`, {
    email: data.email,
    action: "verify-token",
    token: data.token,
    newPassword: data.newPassword,
  });
  return response.data;
};

// Google login using Firebase ID token
export const authGoogleLogin = async (idToken: string) => {
  try {
    const res = await postApi(
      "/api/auth/Google",
      { idToken },
      { "Content-Type": "application/json" }
    );

    const { success, data, message } = res.data ?? {};
    if (success === false) throw new Error(message || "Google login failed");

    const pack = data ?? res.data; // supports {success,data:{user,token}} or {user,token}
    if (!pack?.token || !pack?.user) throw new Error("Invalid Google login response");

    return { token: pack.token, user: pack.user };
  } catch (err: any) {
    const e = new Error(
      err?.response?.data?.message || err.message || "Google login failed"
    ) as Error & { code?: string; status?: number };
    e.code =
      err?.response?.data?.errorCode ||
      err?.response?.data?.error?.errorCode ||
      (err?.response?.status === 404 ? "ACCOUNT_NOT_FOUND" : undefined);
    e.status = err?.response?.status;
    throw e;
  }
};

// Fetch the currently authenticated user's profile
export const fetchUserProfile = async () => {
  try {
    // Call the backend API with authorization headers
    const response = await getApi(`/api/user/Get`, {
      "Content-Type": "application/json",
      ...getAuthHeaders(), // Adds authentication token
    });

    const { success, data, message } = response.data;

    if (!success) throw new Error(message || "Failed to fetch user profile");

    return data; // Returns the user profile object if successful
  } catch (err: any) {
    if (isNetworkError(err)) {
      // Handle network errors gracefully by falling back to localStorage
      try {
        const raw = localStorage.getItem("user");
        if (!raw) throw new Error("No local user available");
        const u = JSON.parse(raw);
        
        // Construct a fallback user object with default/demo values
        return {
          userId: Number(u?.userId || 0),
          email: u?.email || "demo@example.com",
          username: u?.username || "demo",
          firstName: u?.firstName || "Demo",
          lastName: u?.lastName || "User",
          organisation: u?.org || u?.organisation || "",
          avatarUrl: null,
          displayName: `${u?.firstName || "Demo"} ${u?.lastName || "User"}`,
          createdAt: new Date().toISOString(),
        };
      } catch (e: any) {
        // If localStorage fallback fails
        throw new Error(e?.message || "Failed to load user profile (backend unreachable)");
      }
    }
    // Re-throw API or other errors
    throw new Error(err.response?.data?.message || err.message || "Failed to fetch user profile");
  }
};

// Update user's profile data (firstName, lastName, username, organisation)
export const updateUserProfile = async (payload: {
  firstName?: string;
  lastName?: string;
  organisation?: string;
  username?: string;
  roleDescription?: string;
  usagePurpose?: string;
  avatarUrl?: string | null;
  avatarURL?: string | null;
}) => {
  try {
    const response = await axios.put(`${API_BASE}/api/user/Update`, payload, {
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(), // Auth token
      },
    });

    const { success, data, message } = response.data;

    if (!success) throw new Error(message || "Failed to update user profile");

    return data; // Returns the updated profile data
  } catch (err: any) {
    throw new Error(err.response?.data?.message || err.message || "Failed to update user profile");
  }
};
// TOC Project APIs

// Create a new TOC (Table of Contents) project
export const createTocProject = async (data: {
  userId: string;
  projectTitle: string;
  status: "draft" | "published";
}) => {
  try {
    const response = await axios.post(
      `${API_BASE}/api/project/Create`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(), // Auth token
        },
      }
    );
    return response.data; // Returns success/message/data/statusCode from backend
  } catch (err: any) {
    // Offline mode fallback: create a local project ID
    if (isNetworkError(err)) {
      const projectId = `local-${Date.now()}`;
      return {
        success: true,
        message: "Created locally (offline mode)",
        statusCode: 200,
        data: {
          projectId,
          tocData: { projectTitle: data.projectTitle },
          tocColor: {},
        },
      };
    }
    throw new Error(err.response?.data?.message || err.message || "Failed to create project");
  }
};

// Update an existing TOC project
export const updateToc = async (payload: any) => {
  const token = localStorage.getItem("token"); // get the stored token
  if (!token) throw new Error("No authentication token found");

  try {
    const response = await axios.put(
      `${API_BASE}/api/project/Update`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data; // Returns backend success/message/data/statusCode
  } catch (err: any) {
     // Offline save fallback
    if (isNetworkError(err)) {
      return { success: true, message: "Saved locally (offline mode)", data: {}, statusCode: 200 };
    }
    throw new Error(err.response?.data?.message || err.message || "Failed to update project");
  }
};

// Fetch all TOC projects for the authenticated user
export const fetchUserTocs = async () => {
  try {
    const response = await axios.get(`${API_BASE}/api/project/GetProjectList`, {
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(), 
      },
    });
    return response.data; // Returns projects array
  } catch (err: any) {
    const msg = err?.response?.data?.message || err?.message || "Failed to fetch projects";
    // Always return empty array in error cases to avoid breaking UI
    const friendly = /collection/i.test(String(msg))
      ? "We couldn't load your projects right now. Please try again shortly."
      : msg;
    return { success: true, data: { projects: [] }, message: friendly };
  }
};

// Fetch a specific TOC project by its ID
export const fetchTocProjectById = async (projectId: string) => {
  if (!projectId) throw new Error("Project ID is required");

  try {
    const response = await axios.get(`${API_BASE}/api/project/Get`, {
      params: { projectId },
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
    });

    return response.data; // Returns project details: tocData and tocColor
  } catch (err: any) {
    // Offline fallback: return empty project
    if (isNetworkError(err)) {
      return { success: true, data: { projects: [] }, message: "No project loaded (offline mode)" };
    }
    throw new Error(err.response?.data?.message || err.message || "Failed to fetch project");
  }
};

export const deleteTocProject = async (projectId: string) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No authentication token found");

  try {
    const response = await axios.delete(
      `${API_BASE}/api/project/DeleteProject`,
      {
        params: { projectId },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
         } as any
      }
    );

    return response.data;
  } catch (err: any) {
    throw new Error(
      err.response?.data?.message || err.message || "Failed to delete project"
    );
  }
};

export const requestExpertReview = async (projectId?: string | null) => {
  const response = await postApi(
    "/api/review/Request",
    { projectId },
    {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    }
  );

  return response.data;
};

// ---- Subscription / Payment APIs ----
export const createCheckoutSession = async (data: {
  price_id: string;
  user_id: string | number;
  email?: string;
  success_url?: string;
  cancel_url?: string;
}) => {
  try {
    console.debug('API:createCheckoutSession → request', {
      base: PAYMENT_API_BASE,
      price_id: data.price_id,
      user_id: data.user_id,
      email: data.email,
      success_url: data.success_url,
      cancel_url: data.cancel_url,
    });
    const response = await axios.post(
      `${PAYMENT_API_BASE}/api/payment/create-checkout-session`,
      {
        price_id: data.price_id,
        user_id: data.user_id,
        email: data.email,
        success_url: data.success_url,
        cancel_url: data.cancel_url,
      },
      { headers: { "Content-Type": "application/json" } }
    );
    console.debug('API:createCheckoutSession ← response', { status: response.status, data: response.data });
    const { success, url, message } = response.data || {};
    if (!success || !url) {
      throw new Error(message || "Failed to create checkout session");
    }
    return { success, url } as { success: boolean; url: string };
  } catch (err: any) {
    // Fallback: when running locally, always try local stripe backend if primary fails
    console.error('API:createCheckoutSession ✖ error', {
      base: PAYMENT_API_BASE,
      error: err?.response?.data || err,
      message: err?.message,
    });
    const primaryMsg = err.response?.data?.message || err.message || "Failed to create checkout session";
    //const isLocal = typeof window !== 'undefined' && window.location.origin.startsWith('http://localhost');
    //const triedLocalAlready = PAYMENT_API_BASE.startsWith('http://localhost:3001');
    //const shouldTryLocal = isLocal && !triedLocalAlready;
    /*if (shouldTryLocal) {
      try {
        //const altBase = 'http://localhost:3001';
        console.debug('API:createCheckoutSession ↻ retrying against local backend', { altBase });
        const retry = await axios.post(
          `${altBase}/api/payment/create-checkout-session`,
          {
            price_id: data.price_id,
            user_id: data.user_id,
            email: data.email,
            success_url: data.success_url,
            cancel_url: data.cancel_url,
          },
          { headers: { "Content-Type": "application/json" } }
        );
        console.debug('API:createCheckoutSession ← local response', { status: retry.status, data: retry.data });
        const { success, url, message } = retry.data || {};
        if (!success || !url) throw new Error(message || primaryMsg);
        return { success, url } as { success: boolean; url: string };
      } catch (retryErr: any) {
        console.error('API:createCheckoutSession ✖ local retry error', {
          error: retryErr?.response?.data || retryErr,
          message: retryErr?.message,
        });
        throw new Error(retryErr.response?.data?.message || retryErr.message || primaryMsg);
      }
    }*/
    throw new Error(primaryMsg);
  }
};

export const cancelSubscription = async (data: {
  user_id: string | number;
  subscription_id?: string;
  cancel_at_period_end?: boolean;
}) => {
  try {
    const response = await axios.post(
      `${PAYMENT_API_BASE}/api/payment/cancel-subscription`,
      {
        user_id: data.user_id,
        subscription_id: data.subscription_id,
        cancel_at_period_end: data.cancel_at_period_end,
      },
      { headers: { "Content-Type": "application/json" } }
    );

    const { success, message } = response.data || {};
    if (!success) {
      throw new Error(message || "Failed to cancel subscription");
    }
    return { success, message };
  } catch (err: any) {
    // If local dev points to an unavailable port (e.g., 3003), retry on 3001
    const msg = err.response?.data?.message || err.message || "Failed to cancel subscription";
    /*const isLocal = PAYMENT_API_BASE.startsWith("http://localhost");
    const needsPortRetry = isNetworkError(err) && isLocal && /:3003\b/.test(PAYMENT_API_BASE);
    if (needsPortRetry) {
      try {
        const altBase = PAYMENT_API_BASE.replace(':3003', ':3001');
        const retry = await axios.post(
          `${altBase}/api/payment/cancel-subscription`,
          {
            user_id: data.user_id,
            subscription_id: data.subscription_id,
            cancel_at_period_end: data.cancel_at_period_end,
          },
          { headers: { "Content-Type": "application/json" } }
        );
        const { success, message } = retry.data || {};
        if (!success) throw new Error(message || "Failed to cancel subscription");
        return { success, message };
      } catch (retryErr: any) {
        throw new Error(retryErr.response?.data?.message || retryErr.message || msg);
      }
    }*/
    throw new Error(msg);
  }
};

export const syncStripeSubscription = async (data: {
  session_id?: string;
  subscription_id?: string;
  user_id?: string | number;
  email?: string;
}) => {
  try {
    const response = await axios.post(
      `${PAYMENT_API_BASE}/api/payment/update-subscription`,
      {
        session_id: data.session_id,
        subscription_id: data.subscription_id,
        user_id: data.user_id,
        email: data.email,
      },
      { headers: { "Content-Type": "application/json" } }
    );

    const { success, data: payload, message } = response.data || {};
    if (!success) {
      throw new Error(message || "Failed to sync subscription from Stripe");
    }
    return { success: true, data: payload } as {
      success: boolean;
      data: {
        subscriptionId: string;
        email: string;
        planId: string;
        status: string;
        startDate: string;
        renewalDate: string;
        expiresAt: string;
        autoRenew: boolean;
        customerId?: string;
        checkoutSessionId?: string | null;
      };
    };
  } catch (err: any) {
    const primaryMsg = err.response?.data?.message || err.message || "Failed to sync subscription from Stripe";
    /*const isLocal = typeof window !== 'undefined' && window.location.origin.startsWith('http://localhost');
    const triedLocalAlready = PAYMENT_API_BASE.startsWith('http://localhost:3001');
    const shouldTryLocal = isLocal && !triedLocalAlready;
    if (shouldTryLocal) {
      try {
        const altBase = 'http://localhost:3001';
        const retry = await axios.post(
          `${altBase}/api/payment/update-subscription`,
          {
            session_id: data.session_id,
            subscription_id: data.subscription_id,
            user_id: data.user_id,
            email: data.email,
          },
          { headers: { "Content-Type": "application/json" } }
        );
        const { success, data: payload, message } = retry.data || {};
        if (!success) throw new Error(message || primaryMsg);
        return { success: true, data: payload } as {
          success: boolean;
          data: {
            subscriptionId: string;
            email: string;
            planId: string;
            status: string;
            startDate: string;
            renewalDate: string;
            expiresAt: string;
            autoRenew: boolean;
            customerId?: string;
            checkoutSessionId?: string | null;
          };
        };
      } catch (retryErr: any) {
        throw new Error(retryErr.response?.data?.message || retryErr.message || primaryMsg);
      }
    }*/
    throw new Error(primaryMsg);
  }
};

export const updateSubscription = async (data: {
  subscriptionId: string;
  email: string;
  planId: string;
  status: string;
  startDate: string;
  renewalDate: string;
  expiresAt: string;
  autoRenew: boolean;
}) => {
  try {
    // Backend expects EXACT camelCase keys as below
    const payload = {
      subscriptionId: data.subscriptionId,
      email: data.email,
      planId: data.planId,
      status: data.status,
      startDate: data.startDate,
      renewalDate: data.renewalDate,
      expiresAt: data.expiresAt,
      autoRenew: data.autoRenew,
    };

    const url = `${API_BASE}/api/subscription/Create`;
    const response = await axios.post(url, payload, {
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
    });

    const { success, data: resData, message } = response.data;

    if (!success) throw new Error(message || "Failed to update subscription");

    return resData;
  } catch (err: any) {
    throw new Error(err?.response?.data?.message || err.message || "Failed to update subscription");
  }
};

/*export const getSubscriptionPlans = async () => {
  // Temporary: return hardcoded plans until backend endpoint is available
  return [
    {
      id: "free",
      name: "Free",
      stripe_price_id_monthly: "price_free",
      features: ["Basic access"],
    },
    {
      id: "starter",
      name: "Starter",
      stripe_price_id_monthly: "price_1S8tsnQTtrbKnENdYfv6azfr",
      features: ["Form & Visual editor", "Export diagram"],
    },
    {
      id: "pro",
      name: "Pro",
      stripe_price_id_monthly: "price_1SB17tQTtrbKnENdT7aClaEe",
      features: ["Everything in Starter", "Advanced customization"],
    },
  ];
};
*/

// ---- Terms & Conditions APIs ----
export const fetchTerms = async () => {
  let lastMessage = "Failed to fetch terms";

  for (const url of getTermsRequestUrls()) {
    try {
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return parseTermsPayload(response);
    } catch (err: any) {
      lastMessage =
        err.response?.data?.message ||
        err.message ||
        lastMessage;
    }
  } 

    const fallback = await fetchTermsStaticFallback();
  if (fallback) return fallback;

    throw new Error(lastMessage);
};

export const updateTerms = async (content: string) => {
  try {
    const url = resolveAdminTermsApiUrl();
    const response = await axios.post(
      url,
      { content },
      { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
    );

    const body = response.data || {};
    const { success, message, content: saved, updatedAt } = body as {
      success?: boolean;
      message?: string;
      content?: string;
      updatedAt?: string;
    };
    if (!success) {
      throw new Error(message || "Failed to update terms");
    }
    return {
      success: true,
      message: message || "Terms updated successfully",
      content: typeof saved === "string" ? saved : content,
      updatedAt: updatedAt ?? null,
    };
  } catch (err: any) {
    // Fallback for when backend endpoint doesn't exist yet
    if (err.response?.status === 404 || isNetworkError(err)) {
      return {
        success: true,
        message: "Terms updated successfully (local simulation)",
        content,
        updatedAt: null,
      };
    }
    throw new Error(err.response?.data?.message || err.message || "Failed to update terms");
  }
};

/**
 * Fetch user's subscription details
 * @returns Subscription data including plan, status, and renewal info
 */
export const fetchSubscription = async () => {
  const token = localStorage.getItem("token");
  // Avoid hitting protected endpoint before login (e.g., on /login navbar mount)
  if (!token) {
    return {
      success: true,
      data: null,
      message: "No active subscription",
    };
  }
  try {
    const response = await axios.get(`${API_BASE}/api/subscription/Get`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const { success, data, message } = response.data;

    if (!success) {
      // User might not have a subscription yet (free user)
      if (response.data.statusCode === 404) {
        // Fallback to localStorage: prefer unified 'subscriptionData' first
        try {
          const unifiedRaw = localStorage.getItem('subscriptionData');
          if (unifiedRaw) {
            const unified = JSON.parse(unifiedRaw);
            if (unified && unified.status === 'active') {
              return { success: true, data: unified, message: 'Loaded subscription from local storage' };
            }
          }
        } catch {}
        // Legacy fallback: 'userSubscription'
        try {
          const localRaw = localStorage.getItem('userSubscription');
          if (localRaw) {
            const localSub = JSON.parse(localRaw);
            if (localSub && localSub.status === 'active') {
              const planName = String(localSub.plan || '').toLowerCase();
              const envPro = process.env.REACT_APP_STRIPE_PRICE_PRO || '';
              const envPremium = process.env.REACT_APP_STRIPE_PRICE_PREMIUM || '';
              let planId = '';
              if (planName.includes('premium')) planId = envPremium || 'premium';
              else if (planName.includes('pro')) planId = envPro || 'pro';
              else planId = 'free';
              const userRaw = localStorage.getItem('user');
              const email = userRaw ? (JSON.parse(userRaw)?.email || '') : '';
              const nowISO = new Date().toISOString();
              return {
                success: true,
                data: {
                  subscriptionId: localSub.subscriptionId || `local-${Date.now()}`,
                  email,
                  planId,
                  status: 'active',
                  startDate: localSub.activatedAt || nowISO,
                  renewalDate: localSub.expiry ? new Date(localSub.expiry).toISOString() : nowISO,
                  expiresAt: localSub.expiry ? new Date(localSub.expiry).toISOString() : null,
                  autoRenew: true,
                  updatedAt: nowISO,
                },
                message: 'Loaded subscription from local storage',
              };
            }
          }
        } catch {}
        return {
          success: true,
          data: null, // No subscription = free user
          message: 'No active subscription',
        };
      }
      throw new Error(message || 'Failed to fetch subscription');
    }

    // If backend returns no subscription, try localStorage fallback
    if (!data) {
      // Prefer unified key first
      try {
        const unifiedRaw = localStorage.getItem('subscriptionData');
        if (unifiedRaw) {
          const unified = JSON.parse(unifiedRaw);
          if (unified && unified.status === 'active') {
            return { success: true, data: unified, message: 'Loaded subscription from local storage' };
          }
        }
      } catch {}
      // Legacy fallback
      try {
        const localRaw = localStorage.getItem('userSubscription');
        if (localRaw) {
          const localSub = JSON.parse(localRaw);
          if (localSub && localSub.status === 'active') {
            const planName = String(localSub.plan || '').toLowerCase();
            const envPro = process.env.REACT_APP_STRIPE_PRICE_PRO || '';
            const envPremium = process.env.REACT_APP_STRIPE_PRICE_PREMIUM || '';
            let planId = '';
            if (planName.includes('premium')) planId = envPremium || 'premium';
            else if (planName.includes('pro')) planId = envPro || 'pro';
            else planId = 'free';
            const userRaw = localStorage.getItem('user');
            const email = userRaw ? (JSON.parse(userRaw)?.email || '') : '';
            const nowISO = new Date().toISOString();
            return {
              success: true,
              data: {
                subscriptionId: localSub.subscriptionId || `local-${Date.now()}`,
                email,
                planId,
                status: 'active',
                startDate: localSub.activatedAt || nowISO,
                renewalDate: localSub.expiry ? new Date(localSub.expiry).toISOString() : nowISO,
                expiresAt: localSub.expiry ? new Date(localSub.expiry).toISOString() : null,
                autoRenew: true,
                updatedAt: nowISO,
              },
              message: 'Loaded subscription from local storage',
            };
          }
        }
      } catch {}
    }

    return { success: true, data };
  } catch (err: any) {
    // Handle network errors or when backend is unreachable
    if (isNetworkError(err)) {
      // Prefer unified key first
      try {
        const unifiedRaw = localStorage.getItem('subscriptionData');
        if (unifiedRaw) {
          const unified = JSON.parse(unifiedRaw);
          if (unified && unified.status === 'active') {
            return { success: true, data: unified, message: 'Loaded subscription from local storage' };
          }
        }
      } catch {}
      // Legacy fallback
      try {
        const localRaw = localStorage.getItem('userSubscription');
        if (localRaw) {
          const localSub = JSON.parse(localRaw);
          if (localSub && localSub.status === 'active') {
            const planName = String(localSub.plan || '').toLowerCase();
            const envPro = process.env.REACT_APP_STRIPE_PRICE_PRO || '';
            const envPremium = process.env.REACT_APP_STRIPE_PRICE_PREMIUM || '';
            let planId = '';
            if (planName.includes('premium')) planId = envPremium || 'premium';
            else if (planName.includes('pro')) planId = envPro || 'pro';
            else planId = 'free';
            const userRaw = localStorage.getItem('user');
            const email = userRaw ? (JSON.parse(userRaw)?.email || '') : '';
            const nowISO = new Date().toISOString();
            return {
              success: true,
              data: {
                subscriptionId: localSub.subscriptionId || `local-${Date.now()}`,
                email,
                planId,
                status: 'active',
                startDate: localSub.activatedAt || nowISO,
                renewalDate: localSub.expiry ? new Date(localSub.expiry).toISOString() : nowISO,
                expiresAt: localSub.expiry ? new Date(localSub.expiry).toISOString() : null,
                autoRenew: true,
                updatedAt: nowISO,
              },
              message: 'Loaded subscription from local storage',
            };
          }
        }
      } catch {}
      return {
        success: true,
        data: null, // Treat as free user when offline
        message: 'No subscription data available (offline mode)',
      };
    }

    // Handle 404 - user has no subscription
    if (err.response?.status === 404) {
      return {
        success: true,
        data: null,
        message: "No active subscription",
      };
    }

    throw new Error(
      err.response?.data?.message || 
      err.message || 
      "Failed to fetch subscription"
    );
  }
};
const postApi = async (path: string, data: any, headers: any = {}) => {
  try {
    return await axios.post(`${API_BASE}${path}`, data, { headers });
  } catch (err: any) {
    // Case-sensitive backend fallback
    if (err?.response?.status === 404) {
      const alt = altCasePath(path);
      if (alt !== path) {
        try { return await axios.post(`${API_BASE}${alt}`, data, { headers }); } catch (_) {}
      }
    }
    throw err;
  }
};

const getApi = async (path: string, headers: any = {}) => {
  try {
    return await axios.get(`${API_BASE}${path}`, { headers });
  } catch (err: any) {
    if (err?.response?.status === 404) {
      const alt = altCasePath(path);
      if (alt !== path) {
        try { return await axios.get(`${API_BASE}${alt}`, { headers }); } catch (_) {}
      }
    }
    throw err;
  }
  };

  export const getFieldHint = async (payload: {
  field: string;
  value: string;
  projectTitle?: string;
}) => {
  const token = localStorage.getItem("token");
  const controller = new AbortController();
  const timeoutMs = 55_000;
  const killTimer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await axios.post(`${API_BASE}/api/ai/field-hint`, payload, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal: controller.signal,
      timeout: timeoutMs,
    });
    return response.data;
  } finally {
    window.clearTimeout(killTimer);
  }
};
